import { useTranslation } from 'next-i18next';
import { memo, useCallback } from 'react';
import { IconApple, IconFacebook, IconGoogleColor } from '@bridebook/ui/src/icons';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { useAuthContext } from '@bridebook/web/components/auth/auth-context';
import AuthButton from '@bridebook/web/components/auth/signup-container/components/auth-buttons/auth-button/auth-button';
import { LoginOrSignupResult } from '@bridebook/web/lib/api/authenticate/types';
import { disableAuthForm, toggleSelectedCountryError } from '@bridebook/web/lib/auth/actions';
import { useSignUpHelpers } from '@bridebook/web/lib/auth/hooks/use-signup-hooks';
import {
  getAuthFormDisabled,
  getEmailExistsWithDifferentProvider,
  isCountrySelected,
} from '@bridebook/web/lib/auth/selectors';
import { AuthProviders } from '@bridebook/web/lib/auth/types';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { getIsIOS, getIsMacOS } from '@bridebook/web/lib/mobile-app/selectors';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';
import VariantAuthButton from '@bridebook/web/components/auth/signup-container/components/variant-auth-button/variant-auth-button';

const VARIANT_ORDER = ['googleButton', 'appleButton', 'facebookButton'];

interface IProps {
  variant?: boolean;
  showCollaborationAnimationVisible?: (show: boolean) => void;
}

const sortByOrder =
  (order: string[]) =>
  ([a]: [string, unknown], [b]: [string, unknown]) =>
    order.indexOf(a) - order.indexOf(b);

const AuthButtons = ({ variant = false, showCollaborationAnimationVisible }: IProps) => {
  const { t } = useTranslation('signup');
  const {
    loginOrSignupWithGoogle,
    loginOrSignupWithApple,
    loginOrSignupWithFacebook,
    redirectAfterLoginOrSignup,
    prepareRedirectAfterLoginOrSignup,
  } = useAuthContext();
  const dispatch = useDispatch();
  const authFormDisabled = useSelector(getAuthFormDisabled);
  const countrySelected = useSelector(isCountrySelected);
  const market = useMarket();
  const emailExistsWithDifferentProvider = useSelector(getEmailExistsWithDifferentProvider);
  const isiOS = useSelector(getIsIOS);
  const isMacOS = useSelector(getIsMacOS);
  const { isSignUp } = useSignUpHelpers();

  const isAppleVisible = isiOS || isMacOS;

  const toggleError = isSignUp && !countrySelected;

  const handleSocialLogin = useCallback(
    async (provider: Exclude<AuthProviders, AuthProviders.PASSWORD>) => {
      if (toggleError) {
        dispatch(toggleSelectedCountryError(true, provider));
        dispatch(toggleSnackbar('alert', t('pleaseSelectCountry')));
      } else {
        dispatch(disableAuthForm);
        let result: LoginOrSignupResult | undefined;
        const loginOrSignupParams = { countryCode: market.country, locale: market.locale };

        switch (provider) {
          case AuthProviders.APPLE:
            result = await loginOrSignupWithApple(loginOrSignupParams);
            break;
          case AuthProviders.GOOGLE:
            result = await loginOrSignupWithGoogle(loginOrSignupParams);
            break;
          case AuthProviders.FACEBOOK:
            result = await loginOrSignupWithFacebook(loginOrSignupParams);
            break;
        }

        if (result && result.status === 'success' && !result.isCollaborator) {
          await redirectAfterLoginOrSignup(result);
        }
        if (result && result.status === 'success' && result.isCollaborator) {
          prepareRedirectAfterLoginOrSignup(result);
          showCollaborationAnimationVisible?.(true);
        }
      }
    },
    [
      dispatch,
      loginOrSignupWithApple,
      loginOrSignupWithFacebook,
      loginOrSignupWithGoogle,
      market.country,
      market.locale,
      prepareRedirectAfterLoginOrSignup,
      redirectAfterLoginOrSignup,
      showCollaborationAnimationVisible,
      t,
      toggleError,
    ],
  );

  const handleAppleLogin = useCallback(
    () => handleSocialLogin(AuthProviders.APPLE),
    [handleSocialLogin],
  );
  const handleGoogleLogin = useCallback(
    () => handleSocialLogin(AuthProviders.GOOGLE),
    [handleSocialLogin],
  );
  const handleFacebookLogin = useCallback(
    () => handleSocialLogin(AuthProviders.FACEBOOK),
    [handleSocialLogin],
  );

  const authButtonsConfig = {
    ...(isAppleVisible && {
      appleButton: {
        text: variant ? t('continueWithApple') : isSignUp ? t('signupApple') : t('loginApple'),
        onClick: handleAppleLogin,
        icon: <IconApple width={variant ? 16 : 18} color="black" />,
      },
    }),
    ...(emailExistsWithDifferentProvider !== AuthProviders.GOOGLE && {
      googleButton: {
        text: variant ? t('continueWithGoogle') : isSignUp ? t('signupGoogle') : t('loginGoogle'),
        onClick: handleGoogleLogin,
        icon: <IconGoogleColor width={variant ? 16 : 19} />,
      },
    }),
    ...(emailExistsWithDifferentProvider !== AuthProviders.FACEBOOK && {
      facebookButton: {
        text: variant
          ? t('continueWithFacebook')
          : isSignUp
            ? t('signupFacebook')
            : t('loginFacebook'),
        onClick: handleFacebookLogin,
        icon: <IconFacebook color="facebookBlue" width={variant ? 8 : 10} />,
      },
    }),
  };

  if (variant) {
    return (
      <>
        {Object.entries(authButtonsConfig)
          .sort(sortByOrder(VARIANT_ORDER))
          .map(([key, button]) => (
            <VariantAuthButton key={key} disabled={authFormDisabled} size="lg" {...button} />
          ))}
      </>
    );
  }

  return (
    <>
      {Object.entries(authButtonsConfig).map(([key, button]) => (
        <AuthButton key={key} disabled={authFormDisabled} {...button} />
      ))}
    </>
  );
};

export default memo(AuthButtons);
